import type { SystemProps } from '@xstyled/styled-components'
import type { MoteActionButtonProps } from '../../MoteActionButton'
import type { ReactionPickerProps } from '../../ReactionPicker'
import type { TranscriptionPreviewProps } from '../../Transcription'

export enum MoteCardSize {
  SMALL = 'sm',
  MEDIUM = 'md',
  MED_LARGE = 'md-lg',
  LARGE = 'lg',
  XLARGE = 'xl'
}

export const moteCardBreakpoints = {
  [MoteCardSize.MEDIUM]: 224,
  [MoteCardSize.MED_LARGE]: 258,
  [MoteCardSize.LARGE]: 300,
  [MoteCardSize.XLARGE]: 393
}

export type MoteCardConfigWithoutTranslateButton = {
  size: MoteCardSize
  breakpoint: number
  showAvatar: boolean
  firstButtonSkeletonWidth: SystemProps['w']
  secondButtonSkeletonWidth: SystemProps['w']
  actionButtonPlacement: SystemProps['gridTemplateColumns']
  firstButtonTextPosition: MoteActionButtonProps['textPosition']
  secondButtonTextPosition: MoteActionButtonProps['textPosition']
  transcriptRows: TranscriptionPreviewProps['rows']
  reactionPickerPlacement: ReactionPickerProps['placement']
}

export type MoteCardConfig = MoteCardConfigWithoutTranslateButton & {
  translateButtonTextPosition: MoteActionButtonProps['textPosition']
}

export type MoteCardConfigs = Record<MoteCardSize, MoteCardConfig>

export interface MoteCardConfigOptions {
  isCreator: boolean
  isLoopy: boolean
  hasTranscript: boolean
}

const moteCardConfig = (options: MoteCardConfigOptions): MoteCardConfigs => {
  const configs: Record<MoteCardSize, MoteCardConfigWithoutTranslateButton> = {
    [MoteCardSize.SMALL]: {
      showAvatar: false,
      size: MoteCardSize.SMALL,
      breakpoint: moteCardBreakpoints[MoteCardSize.MEDIUM],
      actionButtonPlacement: '32px 1fr 32px',
      firstButtonTextPosition: options.isCreator ? 'tooltip' : 'none',
      secondButtonTextPosition: 'tooltip',
      transcriptRows: 2,
      reactionPickerPlacement: 'top-left',
      firstButtonSkeletonWidth: '32px',
      secondButtonSkeletonWidth: '32px'
    },
    [MoteCardSize.MEDIUM]: {
      showAvatar: false,
      size: MoteCardSize.MEDIUM,
      breakpoint: moteCardBreakpoints[MoteCardSize.LARGE],
      actionButtonPlacement: '1fr 32px 32px',
      firstButtonTextPosition: 'inside',
      secondButtonTextPosition: 'tooltip',
      transcriptRows: 2,
      reactionPickerPlacement: 'top-center',
      firstButtonSkeletonWidth: 'full',
      secondButtonSkeletonWidth: '32px'
    },
    [MoteCardSize.MED_LARGE]: {
      showAvatar: true,
      size: MoteCardSize.MED_LARGE,
      breakpoint: moteCardBreakpoints[MoteCardSize.MED_LARGE],
      actionButtonPlacement: '1fr 32px 32px',
      firstButtonTextPosition: 'inside',
      secondButtonTextPosition: 'tooltip',
      transcriptRows: 3,
      reactionPickerPlacement: 'top-center',
      firstButtonSkeletonWidth: 'full',
      secondButtonSkeletonWidth: '32px'
    },
    [MoteCardSize.LARGE]: {
      showAvatar: true,
      size: MoteCardSize.LARGE,
      breakpoint: moteCardBreakpoints[MoteCardSize.XLARGE],
      actionButtonPlacement: '1fr 1fr 32px',
      firstButtonTextPosition: 'inside',
      secondButtonTextPosition: 'inside',
      transcriptRows: 3,
      reactionPickerPlacement: 'top-center',
      firstButtonSkeletonWidth: 'full',
      secondButtonSkeletonWidth: 'full'
    },
    [MoteCardSize.XLARGE]: {
      showAvatar: true,
      size: MoteCardSize.XLARGE,
      breakpoint: 400,
      actionButtonPlacement: '1fr 1fr 32px',
      firstButtonTextPosition: 'inside',
      secondButtonTextPosition: 'inside',
      transcriptRows: 3,
      reactionPickerPlacement: 'top-center',
      firstButtonSkeletonWidth: 'full',
      secondButtonSkeletonWidth: 'full'
    }
  }

  return Object.entries(configs).reduce((acc, [key, config]) => {
    acc[key as MoteCardSize] = {
      ...config,
      actionButtonPlacement:
        options.isCreator && !options.hasTranscript
          ? '32px 1fr 32px'
          : !options.isCreator
          ? options.hasTranscript
            ? options.isLoopy
              ? 'auto 32px'
              : config.actionButtonPlacement
            : 'auto 32px'
          : config.actionButtonPlacement,
      translateButtonTextPosition: options.isCreator
        ? config.firstButtonTextPosition
        : options.isLoopy
        ? config.firstButtonTextPosition
        : config.secondButtonTextPosition
    }
    return acc
  }, {} as MoteCardConfigs)
}

export function getMoteCardConfig(
  width: number,
  options: MoteCardConfigOptions
): MoteCardConfig {
  const config = moteCardConfig(options)

  if (!width) return config[MoteCardSize.XLARGE]

  if (width < moteCardBreakpoints[MoteCardSize.MEDIUM]) {
    return config[MoteCardSize.SMALL]
  }

  if (width < moteCardBreakpoints[MoteCardSize.MED_LARGE]) {
    return config[MoteCardSize.MEDIUM]
  }

  if (width < moteCardBreakpoints[MoteCardSize.LARGE]) {
    return config[MoteCardSize.MED_LARGE]
  }

  if (width < moteCardBreakpoints[MoteCardSize.XLARGE]) {
    return config[MoteCardSize.LARGE]
  }

  return config[MoteCardSize.XLARGE]
}
